import React from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

// Try to coerce common formatted strings (e.g., "$1,234.56", "(1,234)", "12%") to numbers
function coerceNumber(val: string | number): number | null {
    if (val === null || val === undefined) return null;
    if (typeof val === 'number') {
        return Number.isFinite(val) ? val : null;
    }
    let s = String(val).trim();
    if (!s) return null;
    s = s.replace(/[,$\s]/g, ""); // remove commas, currency symbols, and spaces
    s = s.replace(/%$/g, ""); // strip percent
    const num = Number(s);
    if (Number.isNaN(num)) return null;
    return num;
}

function compareByCol(
    a: Array<string | number>,
    b: Array<string | number>,
    colIndex: number,
    direction: "asc" | "desc"
): number {
    const aNum = coerceNumber(a[colIndex]);
    const bNum = coerceNumber(b[colIndex]);
    if (aNum !== null && bNum !== null) {
        return direction === "asc" ? aNum - bNum : bNum - aNum;
    }
    const aStr = String(a[colIndex] ?? "");
    const bStr = String(b[colIndex] ?? "");
    const cmp = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
    return direction === "asc" ? cmp : -cmp;
}

/**
 * Sorts rows while preserving hierarchy grouping.
 * Rows in the first `depth` columns use 'TOTAL' to mark aggregate/subtotal rows.
 * Groups are sorted by their own subtotal row's sort-column value;
 * leaves inside each group are sorted the same way.
 */
function hierarchicalSort(
    rows: Array<Array<string | number>>,
    sortColIndex: number,
    direction: "asc" | "desc",
    depth: number,
    level: number = 0
): Array<Array<string | number>> {
    if (rows.length === 0) return rows;

    const isTotal = (row: Array<string | number>) =>
        typeof row[level] === 'string' && (row[level] as string).toLowerCase() === 'total';

    // Rows where col[level] === 'TOTAL' are the aggregate header for this group
    const groupTotals = rows.filter(isTotal);
    const nonTotals  = rows.filter(r => !isTotal(r));

    if (level >= depth - 1) {
        // Deepest level – just sort the leaf rows
        const sorted = [...nonTotals].sort((a, b) => compareByCol(a, b, sortColIndex, direction));
        return [...groupTotals, ...sorted];
    }

    // Bucket non-total rows by their value at the current hierarchy column
    const groupMap = new Map<string | number, Array<Array<string | number>>>();
    for (const row of nonTotals) {
        const key = row[level];
        if (!groupMap.has(key)) groupMap.set(key, []);
        groupMap.get(key)!.push(row);
    }

    // Determine each group's sort value from its own subtotal row (col[level+1] === 'TOTAL')
    const groupEntries = Array.from(groupMap.entries());
    const groupSortValues = new Map<string | number, number | string>();
    for (const [key, groupRows] of groupEntries) {
        const subTotalRow = groupRows.find(
            r => typeof r[level + 1] === 'string' && (r[level + 1] as string).toLowerCase() === 'total'
        );
        if (subTotalRow) {
            const num = coerceNumber(subTotalRow[sortColIndex]);
            groupSortValues.set(key, num !== null ? num : String(subTotalRow[sortColIndex] ?? ""));
        }
    }

    // Sort the groups themselves
    groupEntries.sort(([keyA], [keyB]) => {
        const aVal = groupSortValues.get(keyA);
        const bVal = groupSortValues.get(keyB);
        if (typeof aVal === 'number' && typeof bVal === 'number') {
            return direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        const aStr = String(aVal ?? "");
        const bStr = String(bVal ?? "");
        const cmp = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
        return direction === 'asc' ? cmp : -cmp;
    });

    // Recursively sort within each group and flatten
    const result: Array<Array<string | number>> = [...groupTotals];
    for (const [, groupRows] of groupEntries) {
        result.push(...hierarchicalSort(groupRows, sortColIndex, direction, depth, level + 1));
    }
    return result;
}

// Column item
interface ColumnItem {
    name: string;
    sortable?: boolean;
    customFormatting?: (value: string | number) => JSX.Element
    customClassName?: string;
}

interface TableProps {
    columns: Array<string | ColumnItem>;
    data: Array<Array<string | number>>;
    rowClassName?: (rowIndex: number, sortedData?: Array<Array<string | number>>) => string|null;
    defaultSortColumn?: string | null;
    defaultSortDirection?: "asc" | "desc";
    headerClassName?: string;
    stickyHeader?: boolean;
    parentDivClassName?: string;
    // Lazy rendering options
    virtualize?: boolean; // enable incremental rendering
    batchSize?: number; // rows per batch
    // Number of leading columns that represent the hierarchy (enables grouped sort)
    hierarchyDepth?: number;
}

const BasicTable: React.FC<TableProps> = ({
    columns,
    data,
    rowClassName,
    defaultSortColumn = null,
    defaultSortDirection = "desc",
    headerClassName = "px-4 py-2 border border-gray-300 text-left font-medium text-gray-700",
    stickyHeader = true, 
    parentDivClassName: parentDivClassNameProp,
    virtualize = false,
    batchSize = 200,
    hierarchyDepth = 0,
}) => {
    // update the columns to be an array of ColumnItem
    const parsedColumns = columns.map((col) => {
        let item = {} as ColumnItem;
        if (typeof col === "string") {
            item.name = col;
            item.sortable = true;
        } else {
            item = col as ColumnItem;
            item.sortable = item.sortable ?? true;
        }
        return item;
    }) as ColumnItem[];

    const [sortColumn, setSortColumn] = React.useState<string | null>(defaultSortColumn || null);
    const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(defaultSortDirection);
    const [sortedData, setSortedData] = React.useState(data);
    const [renderCount, setRenderCount] = React.useState<number>(virtualize ? Math.min(batchSize, data.length) : data.length);

    const containerRef = React.useRef<HTMLDivElement | null>(null);

    const getSortSymbol = (column: string) => {
        if (sortColumn === column) {
            return sortDirection === "asc" ? <ChevronUp size={16} /> : <ChevronDown size={16} />;
        }
        return <></>;
    };

    const handleSort = (column: string, invertDirection: boolean = true) => {
        const columnItem = parsedColumns.find((col: ColumnItem) => col.name === column);
        if (!columnItem || !columnItem.sortable) return;

        setSortColumn(column);

        let _direction = sortDirection;
        if (invertDirection) {
            _direction = sortColumn === column && _direction === "asc" ? "desc" : "asc";
            setSortDirection(_direction);
        }

        const colIndex = parsedColumns.findIndex((col) => col.name === column);
        const newData = hierarchyDepth > 0
            ? hierarchicalSort([...data], colIndex, _direction, hierarchyDepth)
            : [...data].sort((a, b) => compareByCol(a, b, colIndex, _direction));

        setSortedData(newData);
        // Reset render count after sort for virtualization
        if (virtualize) {
            setRenderCount(Math.min(batchSize, newData.length));
        }
    };

    React.useEffect(() => {
        if (defaultSortColumn) {
            handleSort(defaultSortColumn, false);
        }
    }, [defaultSortColumn]);

    React.useEffect(() => {
        setSortColumn(defaultSortColumn || null);
        setSortDirection(defaultSortDirection);
        setSortedData(data);
        // Reset render count on data change
        setRenderCount(virtualize ? Math.min(batchSize, data.length) : data.length);
    }, [data]);

    // Publish sorted data so export functions can use the current sort order
    React.useEffect(() => {
        (window as any).BasicTableSortedData = sortedData;
        return () => { (window as any).BasicTableSortedData = null; };
    }, [sortedData]);

    const parentDivClassName = parentDivClassNameProp ?? (stickyHeader ? "max-h-screen overflow-y-auto" : "") + " overflow-x-auto";

    const onScroll = React.useCallback(() => {
        if (!virtualize || !containerRef.current) return;
        const el = containerRef.current;
        // When near bottom, load next batch
        const threshold = 150; // px from bottom
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
            setRenderCount(prev => prev < sortedData.length ? Math.min(prev + batchSize, sortedData.length) : prev);
        }
    }, [virtualize, sortedData.length, batchSize]);

    React.useEffect(() => {
        if (!virtualize || !containerRef.current) return;
        const el = containerRef.current;
        const handler = () => onScroll();
        el.addEventListener('scroll', handler);
        return () => el.removeEventListener('scroll', handler);
    }, [virtualize, onScroll]);

    const rowsToRender = virtualize ? sortedData.slice(0, renderCount) : sortedData;

    return (
        <div className={parentDivClassName} ref={containerRef}>
            <table className="min-w-full border-collapse border border-gray-300">
                <thead>
                    <tr className="bg-gray-100">
                        {parsedColumns.map((column, index) => {
                            let finalHeaderClassName = headerClassName;
                            if (column.sortable) {
                                finalHeaderClassName += " cursor-pointer";
                            }
                            if (stickyHeader) {
                                finalHeaderClassName += " sticky top-0 z-10";
                            }

                            let spanStyle = "flex"
                            if (finalHeaderClassName.includes("text-left")) {
                                spanStyle += " justify-start"
                            } else if (finalHeaderClassName.includes("text-center")) {
                                spanStyle += " justify-center"
                            } else if (finalHeaderClassName.includes("text-right")) {
                                spanStyle += " justify-end"
                            }

                            return (
                                <th
                                    key={index}
                                    className={finalHeaderClassName}
                                    onClick={() => column.sortable && handleSort(column.name)}
                                >
                                    <span className={spanStyle}>
                                        {column.name}
                                        {column.sortable && (
                                            <span className="ml-2 text-gray-500">
                                                {getSortSymbol(column.name)}
                                            </span>
                                        )}
                                    </span>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {rowsToRender.map((row, rowIndex) => {
                        const customClassNameReturn = rowClassName ? rowClassName(rowIndex, rowsToRender) : null;
                        let computedRowClassName = customClassNameReturn ? customClassNameReturn : (rowIndex % 2 === 0 ? 'bg-gray-50 hover:bg-gray-200' : 'hover:bg-gray-200');
                        return (
                            <tr
                                key={rowIndex}
                                className={computedRowClassName}
                            >
                                {row.map((cell, cellIndex) => {
                                    const column = parsedColumns[cellIndex];
                                    if (!column) {
                                        return <td key={cellIndex} className="px-2 py-2 border border-gray-300 text-gray-700">N/A</td>;
                                    }
                                    const formattedCell = column.customFormatting ?
                                        column.customFormatting(cell) :
                                        cell;

                                    let className;
                                    if (!customClassNameReturn) {
                                        className = column.customClassName ? column.customClassName : "px-2 py-2 border border-gray-300 text-gray-700";
                                    } else {
                                        className = "px-2 py-2 border"
                                    }

                                    return (
                                        <td
                                            key={cellIndex}
                                            className={className}
                                        >
                                            {formattedCell}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                    {virtualize && renderCount < sortedData.length && (
                        <tr>
                            <td colSpan={parsedColumns.length} className="px-2 py-2 text-center text-gray-500">Loading more...</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default BasicTable;